import { updateTask } from '@commenda/api/service-request';
import { Button, Icons, useToggle } from '@commenda/kakashi';
import { getServiceRequestServiceId } from '@commenda/types';
import { documentName } from '@commenda/utils';
import {
  CorporationType,
  IncorporationRequestStatus,
  LegacyDocumentType,
  TaskParty,
  TaskStatus,
} from '@prisma/client';
import { ConfirmationModal } from '../../../molecules/modals/ConfirmationModal';
import { useState } from 'react';
import { useNotification } from '../../../hooks/useNotification';
import DocumentPreview from '../../common/document-preview';
import { ServiceRequestTaskLabel } from '../../common/labels';
import { ServiceRequestTaskIconConfig } from '../../common/taskIconConfig';
import { WorkflowProcessDrawer } from '../../common/workflow-process-drawer';
import { ServiceRequestTaskCard } from '../../layout/components/taskCard/ServiceRequestTaskCard';
import { ServiceRequestTaskCardConfig, TCommonTaskProp } from '../../types';
import {
  areDocumentsPresent,
  isDocumentPresent,
  updateTaskStatus,
} from '../../utils';
import { USCCORPIncorporationTasks } from '../CCORP/config';

export const USUploadIncorporationDocuments = ({
  task,
  context,
  isTimelineView,
  displayNumber,
}: TCommonTaskProp) => {
  const { role, serviceRequest, mutate } = context;
  const allTasks = serviceRequest.taskGroups.flatMap(
    (eachGroup) => eachGroup.tasks
  );

  const isUploadByLawsTaskGenerated = allTasks.find(
    (eachTask) =>
      eachTask.legacyServiceRequestTaskTaskId === USCCORPIncorporationTasks.UPLOAD_SIGNED_BYLAWS
  );

  const { setNotification } = useNotification();

  // TODO: Should come from corpByJurisdiction Backend config

  let documents: LegacyDocumentType[];

  const businessEntityType = serviceRequest.commendaService?.businessEntityType;
  const isLlc = businessEntityType
    ? businessEntityType === CorporationType.LLC
    : getServiceRequestServiceId(serviceRequest) === 'EM_INC_US_LLC';

  if (isLlc) {
    documents = [
      LegacyDocumentType.CertificateOfIncorporation,
      LegacyDocumentType.ActionBySoleIncorporator,
      LegacyDocumentType.OperatingAgreement,
    ];
  } else {
    documents = [
      LegacyDocumentType.CertificateOfIncorporation,
      LegacyDocumentType.ActionBySoleIncorporator,
      ...(isUploadByLawsTaskGenerated ? [] : [LegacyDocumentType.Bylaws]),
    ];
  }

  const isTaskCompleted = areDocumentsPresent({
    documentTypes: documents,
    serviceRequest,
  });

  const workflowProcessDrawerVisibility = useToggle();
  const confirmationModalVisibility = useToggle();

  const config: ServiceRequestTaskCardConfig = {
    getLeftIcon: (status) => ServiceRequestTaskIconConfig[status],
    getStateConfig: (state) => {
      const stateMap = {
        [TaskParty.CLIENT]: {
          [TaskStatus.COMPLETED]: {
            actions: (
              <Button
                size="sm"
                variant="secondary"
                onClick={workflowProcessDrawerVisibility.show}
              >
                View <Icons.ArrowNarrowRight className="size-4" />
              </Button>
            ),
            label: (
              <ServiceRequestTaskLabel
                label="Incorporation documents uploaded by Commenda"
                status={task.status}
                timestamp={task.updatedAt}
              />
            ),
          },
          [TaskStatus.TO_DO]: {
            actions: null,
            label: (
              <ServiceRequestTaskLabel label="Commenda uploads incorporation documents" />
            ),
          },
          [TaskStatus.BACKLOG]: {
            actions: null,
            label: (
              <ServiceRequestTaskLabel label="Commenda uploads incorporation documents" />
            ),
          },
        },
        [TaskParty.AGENT]: {
          [TaskStatus.COMPLETED]: {
            actions: (
              <Button
                size="sm"
                variant="secondary"
                onClick={workflowProcessDrawerVisibility.show}
              >
                View <Icons.ArrowNarrowRight className="size-4" />
              </Button>
            ),
            label: (
              <ServiceRequestTaskLabel
                label="Incorporation documents uploaded"
                status={task.status}
                timestamp={task.updatedAt}
              />
            ),
          },
          [TaskStatus.TO_DO]: {
            actions: (
              <Button
                size="sm"
                onClick={workflowProcessDrawerVisibility.show}
                variant="primary"
              >
                Start <Icons.ArrowNarrowRight className="size-4" />
              </Button>
            ),
            label: (
              <ServiceRequestTaskLabel label="Upload incorporation documents" />
            ),
          },
          [TaskStatus.IN_PROGRESS]: {
            actions: (
              <Button
                size="sm"
                variant="secondary"
                onClick={workflowProcessDrawerVisibility.show}
              >
                Continue <Icons.ArrowNarrowRight className="size-4" />
              </Button>
            ),
            label: (
              <ServiceRequestTaskLabel
                label="Upload incorporation documents"
                status={task.status}
                timestamp={task.updatedAt}
              />
            ),
          },
          [TaskStatus.BACKLOG]: {
            actions: (
              <Button size="sm" disabled onClick={() => {}} variant="primary">
                Start <Icons.ArrowNarrowRight className="size-4" />
              </Button>
            ),
            label: (
              <ServiceRequestTaskLabel label="Upload incorporation documents" />
            ),
          },
        },
      };

      return (
        stateMap[state.role]?.[state.status] ?? {
          label: task.title,
          actions: null,
        }
      );
    },
  };

  const steps = documents.map((eachDocument) => ({
    heading: documentName(eachDocument),
    key: eachDocument,
    description: '',
    status: isDocumentPresent({
      documentType: eachDocument,
      serviceRequest,
    })
      ? IncorporationRequestStatus.Completed
      : IncorporationRequestStatus.NotStarted,
    children: (
      <DocumentPreview
        serviceRequestTaskId={task.id}
        serviceRequest={serviceRequest}
        mutate={mutate}
        documentType={eachDocument}
        viewOnly={task.pendingOn !== role}
        onUploadSuccess={async () => {
          if (task.status === TaskStatus.TO_DO) {
            const res = await updateTask({
              id: task.id,
              data: { status: TaskStatus.IN_PROGRESS },
            });
            if (!res.ok) {
              setNotification({
                show: true,
                valid: false,
                title: 'Error',
                message: 'Failed to update the task status.',
              });
            }
          }
        }}
      />
    ),
  }));

  const [activeParentStepId, setActiveParentStepId] = useState<string>(
    steps[0].key
  );

  const markTaskAsComplete = async () => {
    const res = await updateTaskStatus({
      task,
      status: TaskStatus.COMPLETED,
      mutate,
    });
    if (!res) {
      setNotification({
        show: true,
        valid: false,
        title: 'Error',
        message: 'Failed to update the task status.',
      });
    }
    workflowProcessDrawerVisibility.toggle();
    confirmationModalVisibility.hide();
  };

  return (
    <div>
      <WorkflowProcessDrawer
        isShown={workflowProcessDrawerVisibility.visible}
        onClose={workflowProcessDrawerVisibility.hide}
        activeParentStepId={activeParentStepId}
        setActiveParentStepId={setActiveParentStepId}
        hideHeader
        header={
          <div className="w-full flex justify-between mr-4 items-center">
            <h3 className="txt-large-medium text-text-secondary ">
              Upload incorporation documents
            </h3>
            <div className="flex items-center gap-2">
              {task.status !== TaskStatus.COMPLETED ? (
                <Button
                  variant="primary"
                  size="sm"
                  disabled={!isTaskCompleted}
                  className="txt-small-medium"
                  onClick={confirmationModalVisibility.show}
                >
                  <Icons.Check className="w-4 h-4" /> Mark task as complete
                </Button>
              ) : null}
            </div>
          </div>
        }
        steps={steps}
      />

      <ServiceRequestTaskCard
        task={task}
        state={{ role, status: task.status }}
        config={config}
        context={context}
        isTimelineView={isTimelineView}
        displayNumber={displayNumber}
      />
      <ConfirmationModal
        isOpen={confirmationModalVisibility.visible}
        onClose={confirmationModalVisibility.hide}
        onConfirm={() => markTaskAsComplete()}
        title="Mark task as complete."
        message="Are you sure you want to mark this task as complete? This action cannot be undone."
        hideCancelButton
      />
    </div>
  );
};
